import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { AuthCallback } from "@/components/AuthCallback";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import ChooseRole from "@/pages/ChooseRole";
import Explore from "@/pages/Explore";
import ModelProfilePage from "@/pages/ModelProfilePage";
import BookingFlow from "@/pages/BookingFlow";
import Bookings from "@/pages/Bookings";
import Wallet from "@/pages/Wallet";
import Licenses from "@/pages/Licenses";
import Dashboard from "@/pages/Dashboard";
import ModelForm from "@/pages/ModelForm";
import Onboarding from "@/pages/Onboarding";
import Studio from "@/pages/Studio";
import TwinActivity from "@/pages/TwinActivity";
import VerifyRegistry from "@/pages/VerifyRegistry";

const Protected = ({ children, allowNoRole = false }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="page-container py-20 text-center text-sm text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/auth" replace />;
  // Google sign-ups must pick a role once before using the app
  if (!user.role && !allowNoRole) return <Navigate to="/auth/role" replace />;
  return children;
};

function RouterContent() {
  const location = useLocation();
  // Emergent Google Auth returns to {origin}/dashboard#session_id=...
  // This synchronous render-time check processes the session_id BEFORE any
  // protected route can run (per the Emergent Auth playbook).
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <Navbar />
      <main className="min-h-[calc(100vh-4rem)]">
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/auth/role" element={<Protected allowNoRole><ChooseRole /></Protected>} />
          <Route path="/explore" element={<Explore />} />
          <Route path="/verify" element={<VerifyRegistry />} />
          <Route path="/models/new" element={<Protected><ModelForm /></Protected>} />
          <Route path="/models/:id/edit" element={<Protected><ModelForm /></Protected>} />
          <Route path="/models/:id/onboarding" element={<Protected><Onboarding /></Protected>} />
          <Route path="/models/:id" element={<ModelProfilePage />} />
          <Route path="/book/:modelId" element={<Protected><BookingFlow /></Protected>} />
          <Route path="/studio" element={<Protected><Studio /></Protected>} />
          <Route path="/twin" element={<Protected><TwinActivity /></Protected>} />
          <Route path="/bookings" element={<Protected><Bookings /></Protected>} />
          <Route path="/wallet" element={<Protected><Wallet /></Protected>} />
          <Route path="/licenses" element={<Protected><Licenses /></Protected>} />
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </>
  );
}

function AppRoutes() {
  return (
    <BrowserRouter>
      <RouterContent />
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
